/** @format */

import { ShoppingCart } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	readPromo,
	removeFromCart,
	saveCartSummary,
	updateProductQuantity,
	addToCart,
	resetCart,
} from "../actions/cartActions";
import { customizeProduct } from "../actions/blindActions";
import { removeEmailFromCartMailChimp } from "../actions/userActions";
import { AnimatePresence } from "framer-motion";
import { Toast } from "./customize/Toast";
import { CartItem } from "../components/CartItem";
import { CartSummary } from "../components/CartSummary";
import { calculateTotals } from "../utils/calulateTotals";

const CartScreen = () => {
	const history = useNavigate();
	const dispatch = useDispatch();
	const cart = useSelector((state) => state.cart);
	const { cartItems, cartSummary } = cart;
	const userLogin = useSelector((state) => state.userLogin);
	const { userInfo } = userLogin;
	const userId = userInfo?.id;
	const userEmail = userInfo?.email;
	const country = useSelector((state) => state.country);
	const [promoCode, setPromoCode] = useState(cartSummary?.promoCode || "");
	const [promoError, setPromoError] = useState("");
	const [toast, setToast] = useState(null);
	const [promoAmount, setPromoAmount] = useState(
		cartSummary?.withPromoPrice || 0
	);
	const totals = calculateTotals(cartItems, country, 0, promoAmount);

	// Cart management functions
	const handleQuantityChange = (index, quantity) => {
		if (quantity < 1) return;
		dispatch(updateProductQuantity(index, quantity));
	};

	const handleRemove = (index) => {
		if (cartItems.length - 1 === 0) {
			dispatch(resetCart());
		} else dispatch(removeFromCart(index));
	};

	const handleDuplicate = (index) => {
		dispatch(addToCart(cartItems[index]));
	};

	const handleEdit = (index) => {
		const itemToEdit = cartItems[index];
		itemToEdit.optionInfo.editId = index;

		dispatch(customizeProduct(itemToEdit));

		history(
			itemToEdit?.productInfo?.Program.includes("Customized")
				? `/customize/details`
				: `${encodeURI(
						`/shop/${encodeURIComponent(
							itemToEdit?.productInfo?.Type.toLowerCase()
						)}/${encodeURIComponent(
							itemToEdit?.productInfo?.New_Program.replaceAll(" ", "_")
						)}`
				  )}`
		);
	};

	// Promo code handling
	const handlePromoApply = async () => {
		try {
			const res = await readPromo(promoCode, userId || -1);
			const promoData = res.data[0];
			const cartTotal = calculateTotals(
				cartItems,
				country,
				0,
				promoAmount
			).ourPrice;
			// Validate referral code
			if (promoData?.Description === "referral" && !userId) {
				setPromoError("Please login to use a referral code");
				return;
			}

			// Validate minimum purchase
			if (
				(promoData?.Description === "New Promo" ||
					promoData?.Description === "wallpaper") &&
				cartTotal <= 100
			) {
				setPromoError("Minimum $100 purchase is required");
				return;
			}

			// Check if promo is expired or invalid
			if (promoData?.Expired) {
				setPromoError("This promo code has expired");
				return;
			}

			// Apply promo
			const promoValue = promoData?.percentage
				? (cartTotal * promoData.percentage) / 100
				: parseFloat(promoData.val);

			setToast({
				type: "success",
				message: "Promo code applied successfully",
			});

			setPromoAmount(promoValue);

			return promoValue;
		} catch (error) {
			console.log("Error applying promo:", error);
			setPromoError("Invalid promo code");
			return 0;
		}
	};

	// Checkout handling
	const handleCheckout = async (e) => {
		e.preventDefault();
		try {
			const totals = calculateTotals(cartItems, country, 0, promoAmount);

			await dispatch(
				saveCartSummary({
					FINAL_MSRP: totals.msrp,
					FINAL_PRICE: totals.ourPrice,
					FINAL_TAX: 0,
					SUBTOTAL: totals.ourPrice - (promoAmount || 0),
					SAVINGS: totals.savings,
					promoCode: promoCode,
					withPromoPrice: promoAmount || 0,
				})
			);

			history("/placeorder", { state: { prevPath: "cart" } });
		} catch (error) {
			console.error("Error proceeding to checkout:", error);
		}
	};

	useEffect(() => {
		if (cartItems.length === 0 && userEmail) {
			removeEmailFromCartMailChimp(userEmail);
		}
	}, [history, cartItems, country]);

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-8">
					<ShoppingCart className="w-8 h-8 text-primary" />
					<h1 className="text-2xl font-bold">Shopping Cart</h1>
				</div>

				{cartItems.length === 0 ? (
					<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
						<p className="text-gray-500 mb-4">Your cart is empty</p>
						<button
							onClick={() => history("/shop")}
							className="px-6 py-2 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-colors"
						>
							Continue Shopping
						</button>
					</div>
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						{/* Cart Items */}
						<div className="lg:col-span-2 space-y-4">
							{totals.items.map((item, index) => (
								<CartItem
									key={index}
									item={item}
									index={index}
									onQuantityChange={handleQuantityChange}
									onRemove={handleRemove}
									onEdit={handleEdit}
									onDuplicate={handleDuplicate}
								/>
							))}
						</div>

						{/* Summary */}
						<div className="lg:col-span-1">
							{cartItems.length !== 0 && (
								<CartSummary
									totals={totals}
									promoCode={promoCode}
									onPromoChange={(e) => setPromoCode(e.target.value)}
									onPromoApply={handlePromoApply}
									promoError={promoError}
									onCheckout={handleCheckout}
									setPromoAmount={setPromoAmount}
									promoAmount={promoAmount}
								/>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Toast Notifications */}
			<AnimatePresence>
				<Toast
					show={toast}
					type={toast?.type}
					message={toast?.message}
					onClose={() => setToast(null)}
				/>
			</AnimatePresence>
		</div>
	);
};

export default CartScreen;
